import { useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import type { PanelState } from '../types';

export const useFullScreen = () => {
  const { state, dispatch } = useAppContext();
  const currentMode = state.panelState.fullScreenMode;

  const setFullScreenMode = useCallback((mode: PanelState['fullScreenMode']) => {
    dispatch({ type: 'SET_FULLSCREEN_MODE', payload: mode });
  }, [dispatch]);

  const toggleFullScreen = useCallback(() => {
    if (currentMode === 'none') {
      setFullScreenMode('split');
    } else {
      setFullScreenMode('none');
    }
  }, [currentMode, setFullScreenMode]);

  const enterEditorFocus = useCallback(() => {
    setFullScreenMode('editor');
  }, [setFullScreenMode]);

  const enterPreviewFocus = useCallback(() => {
    setFullScreenMode('preview');
  }, [setFullScreenMode]);

  const exitFullScreen = useCallback(() => {
    setFullScreenMode('none');
  }, [setFullScreenMode]);

  // Keyboard shortcuts for full-screen mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // F11 for full-screen toggle
      if (event.key === 'F11') {
        event.preventDefault();
        toggleFullScreen();
        return;
      }

      // Escape to exit full-screen
      if (event.key === 'Escape' && currentMode !== 'none') {
        event.preventDefault();
        exitFullScreen();
        return;
      }

      // Additional shortcuts when in full-screen mode
      if (currentMode !== 'none' && (event.metaKey || event.ctrlKey)) {
        switch (event.key) {
          case '1':
            event.preventDefault();
            enterEditorFocus();
            break;
          case '2':
            event.preventDefault();
            enterPreviewFocus();
            break;
          case '3':
            event.preventDefault();
            setFullScreenMode('split');
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentMode, toggleFullScreen, exitFullScreen, enterEditorFocus, enterPreviewFocus, setFullScreenMode]);

  // Apply full-screen classes to body
  useEffect(() => {
    const body = document.body;
    
    if (currentMode !== 'none') {
      body.classList.add('full-screen-mode');
    } else {
      body.classList.remove('full-screen-mode');
    }

    // Cleanup on unmount
    return () => {
      body.classList.remove('full-screen-mode');
    };
  }, [currentMode]);

  const isFullScreen = currentMode !== 'none';
  const isEditorFocused = currentMode === 'editor';
  const isPreviewFocused = currentMode === 'preview';
  const isSplitMode = currentMode === 'split';

  return {
    // State
    currentMode,
    isFullScreen,
    isEditorFocused,
    isPreviewFocused,
    isSplitMode,

    // Actions
    setFullScreenMode,
    toggleFullScreen,
    enterEditorFocus,
    enterPreviewFocus,
    exitFullScreen,
  };
};